import React, { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import classes from "./defaultLayout.module.scss";
import Sidebar from "./Sidebar";
import Header from "./Header";
import StatusBar from "./StatusBar";
import BugReportButton from "../components/BugReportButton";
import CommandPalette from "../components/CommandPalette";
import { getJwtToken } from "../utils/token";
import { PageTitleContext } from "./pageTitleContext";

const COLLAPSED_STORAGE_KEY = "crx.sidebarCollapsed";
const MOBILE_BREAKPOINT = 768;

const readCollapsed = (): boolean => {
    try {
        const stored = localStorage.getItem(COLLAPSED_STORAGE_KEY);
        if (stored !== null) return JSON.parse(stored) === true;
        // Older builds stored the inverse under "sidebarClose" (true = open).
        const legacy = localStorage.getItem("sidebarClose");
        return legacy !== null ? JSON.parse(legacy) === false : false;
    } catch {
        return false;
    }
};

// Persistent layout route: App.tsx mounts this once as the parent of every
// private route and the active page renders into the <Outlet />. The tab title
// is set per page (PageTitle in App.tsx) and shared with the header here.
const DefaultLayout = () => {
    const [isCollapsed, setIsCollapsed] = useState<boolean>(readCollapsed);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);
    const [isMobileMenuVisible, setIsMobileMenuVisible] = useState(false);
    const [pageTitle, setPageTitle] = useState("");
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= MOBILE_BREAKPOINT;
            setIsMobile(mobile);
            if (!mobile) setIsMobileMenuVisible(false);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (!getJwtToken()) {
            navigate("/login");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setIsMobileMenuVisible(false);
    }, [location.pathname]);

    const handleToggleCollapse = () => {
        setIsCollapsed((prev) => {
            const next = !prev;
            try {
                localStorage.setItem(COLLAPSED_STORAGE_KEY, JSON.stringify(next));
            } catch {
                // storage blocked: the choice lasts for this tab only
            }
            return next;
        });
    };

    const pageTitleValue = useMemo(() => ({ pageTitle, setPageTitle }), [pageTitle]);
    const isRail = isCollapsed && !isMobile;

    return (
        <PageTitleContext.Provider value={pageTitleValue}>
            <div className={`${classes.appShell} ${isRail ? classes.appShellRail : ""} ${isMobile ? classes.appShellMobile : ""}`}>
                <Header
                    isCollapsed={isRail}
                    isMobile={isMobile}
                    onMobileMenuClick={() => setIsMobileMenuVisible(true)}
                />
                <Sidebar
                    isCollapsed={isCollapsed}
                    onToggleCollapse={handleToggleCollapse}
                    isMobile={isMobile}
                    isMobileVisible={isMobileMenuVisible}
                    onMobileClose={() => setIsMobileMenuVisible(false)}
                />
                <main className={classes.content}>
                    <div className={classes.childrenWrapper}>
                        <Outlet />
                    </div>
                </main>
                {!isMobile && <StatusBar />}
            </div>
            {/* Outside the layout tree so a page-level crash can't take them down.
                The bug report opens from the sidebar / account menu, or from its
                own floating button while a side drawer covers the sidebar. */}
            <BugReportButton />
            {/* Ctrl+K from anywhere — listens globally, renders nothing until opened. */}
            <CommandPalette />
        </PageTitleContext.Provider>
    );
};

export default DefaultLayout;
